// @flow
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Divider, Typography} from '@material-ui/core';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import makeStyles from '@material-ui/core/styles/makeStyles';

import CustomModal from '../../../elements/CustomModal/CustomModal';

import {colors} from '../../../assets/styles/colors';
import {getImageUrl} from '../../../utils/image_provider';
import {commaFormat} from '../../../utils/number';

import type {
  ManualOrderAction,
  ShoppingCartPricesRequest,
  ShoppingCartProductOptionRequest,
} from '../duck';

import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import type {RootState} from '../../index';
import type {Dispatch} from 'redux';
import {actions as ManualOrderActions} from '../duck';
import Quantity from '../../../elements/Quantity/Quantity';
import {ShoppingCartOutlined} from '@material-ui/icons';

import type {
  Product,
  ProductVariant,
  Currency,
  ShoppingCart,
  CartSummaryItem,
  OrderItemProductOption,
} from '../../../entities';
import {getProductItemPricing} from '../../../utils/product';
import ProductOptionsRenderer from './ProductOptionsRenderer';
import {
  validateAllProductOptionGroups,
  hasValidationErrors,
} from '../utils/productOptionValidation';
import type {ProductOptionGroupValidationErrors} from '../types/productOptions';

export default connect(
  (state: RootState) => ({
    currency: state.currency.currency,
    shoppingCart: state.manualOrder.shoppingCart,
    itemPrices: state.manualOrder.productItemPrices,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        ...ManualOrderActions,
      },
      dispatch
    )
)(ProductConfigurationModal);

type Props = {
  open: boolean,
  product?: Product,
  currency?: Currency,
  shoppingCart?: ShoppingCart,
  onClose: () => void,
  fetchProductPrice: (payload: ManualOrderAction) => void,
  itemPrices?: CartSummaryItem[],
  onAddToCart: (
    product: Product,
    options: {
      productVariant?: ProductVariant,
      productOptions?: OrderItemProductOption[],
      quantity: number,
    }
  ) => void,
};
const useStyles = makeStyles(theme => ({
  modalPaper: {
    padding: '.75rem',
    borderRadius: '.75rem',
    width: '95vw',
    maxWidth: '700px',
    paddingBottom: '0',
    [theme.breakpoints.up('md')]: {
      padding: '1rem',
      paddingBottom: '0',
    },
    [theme.breakpoints.up('lg')]: {
      padding: '1.5rem',
      paddingBottom: '0',
      maxWidth: '900px',
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '600px',
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  divider: {
    margin: '.75rem',
    background: colors.grey300,
  },
  productInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    [theme.breakpoints.up('sm')]: {
      flexWrap: 'nowrap',
    },
  },
  productImage: {
    width: '200px',
    height: '200px',
    borderRadius: '12px',
    margin: '0 auto',
  },
  productDetails: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  productName: {
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  categoriesWrap: {
    marginTop: theme.spacing(1),
    display: 'flex',
  },
  categoryPill: {
    padding: '3px 16px',
    marginRight: '8px',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '12px',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey800,
  },
  subtitle: {
    marginTop: theme.spacing(1),
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  productPrice: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  productNormalPrice: {
    fontSize: '1.125rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  productDiscountedPrice: {
    marginLeft: theme.spacing(1),
    // fontSize: '1.125rem',
    color: colors.grey600,
    textDecoration: 'line-through',
    whiteSpace: 'nowrap',
  },
  quantityWrap: {
    // marginTop: theme.spacing(1),
  },
  quantityRow: {
    marginTop: 4,
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  outOfStock: {
    marginTop: theme.spacing(1),
    color: colors.error,
    fontSize: '12px',
    fontWeight: 600,
    position: 'absolute',
    top: 40,
    width: 230,
  },
  totalPrice: {
    textAlign: 'right',
  },
  totalPriceSubtitle: {
    fontSize: '0.75rem',
    fontWeight: 500,
    textAlign: 'right',
    marginRight: '10px',
  },
  priceTotal: {
    fontWeight: 600,
    fontSize: '1rem',
    whiteSpace: 'nowrap',
    position: 'relative',
  },
  productDiscountedTotalPrice: {},
  variantsWrap: {
    marginTop: theme.spacing(2),
  },
  optionTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  variantPill: {
    backgroundColor: 'transparent',
    padding: '4px 24px',
    margin: '8px 8px 0 0',
    cursor: 'pointer',
    border: `1px solid ${colors.grey400}`,
    borderRadius: '8px',
    textAlign: 'unset',
    textDecoration: 'none',
    outline: 'none',
    color: colors.grey600,
    fontWeight: 500,
    fontSize: '0.75rem',
    '&:last-child': {
      marginRight: 0,
    },
  },
  variantSelected: {
    backgroundColor: colors.lightPurple,
    border: '1px solid rgba(123, 57, 255, 0.3)',
    color: colors.purple3,
    fontWeight: 600,
  },
  bulkPriceTable: {
    alignSelf: 'center',
    background: colors.grey200,
    padding: theme.spacing(2),
    borderRadius: '16px',
    marginBottom: theme.spacing(3),
  },
  bulkPricesTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  bulkPricesTitleIcon: {
    marginRight: theme.spacing(1),
  },
  bulkPricesTitleText: {
    fontWeight: 600,
    color: colors.grey700,
  },
  bulkPricesTableLines: {
    borderRadius: '8px',
    marginTop: theme.spacing(2),
    background: 'white',
    width: '100%',
  },
  bulkPricesTableLineHead: {
    textAlign: 'left',
    padding: '8px 16px',
    fontWeight: 500,
  },
  bulkPricesTableLineData: {
    borderTop: `1px solid ${colors.grey300}`,
    textAlign: 'left',
    padding: '8px 16px',
    fontWeight: 500,
  },
  footer: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem .75rem 1rem .75rem',
    backgroundColor: 'white',
  },
  confirmButton: {
    alignSelf: 'center',
    height: 48,
    width: 220,
    fontSize: '1rem',
    fontWeight: 600,
  },
  // Product options styles
  productOptionsWrap: {
    marginTop: theme.spacing(2),
  },
  optionGroupTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
  },
  optionGroupDescription: {
    fontSize: '0.75rem',
    color: colors.grey600,
    marginBottom: theme.spacing(2),
  },
  optionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    margin: '8px 0',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: colors.purple3,
    },
  },
  optionItemSelected: {
    backgroundColor: colors.lightPurple,
    borderColor: colors.purple3,
  },
  optionItemContent: {
    flex: 1,
    marginLeft: theme.spacing(2),
  },
  optionItemName: {
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  optionItemPrice: {
    fontSize: '0.75rem',
    color: colors.grey600,
    marginTop: '2px',
  },
  optionColorIndicator: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: `2px solid ${colors.grey300}`,
    marginLeft: theme.spacing(1),
  },
  // Simplified styles for options container
  optionsContainer: {
    marginTop: theme.spacing(2),
  },
  quantityControl: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    gap: theme.spacing(1),
  },
  quantityLabel: {
    fontSize: '0.75rem',
    color: colors.grey600,
    minWidth: '60px',
  },
}));

function ProductConfigurationModal(props: Props) {
  const {
    open,
    product,
    currency,
    shoppingCart,
    fetchProductPrice,
    itemPrices,
    onAddToCart,
    onClose,
  } = props;

  const classes = useStyles();
  // const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const [selectedOptions, setSelectedOptions] = useState<{
    [key: string]: string,
  }>({});

  const [selectedProductOptions, setSelectedProductOptions] = useState<{
    [groupId: string]: {
      [optionId: string]: {
        selected: boolean,
        quantity?: number,
        textValue?: string,
      },
    },
  }>({});

  const [
    optionValidationErrors,
    setOptionValidationErrors,
  ] = useState<ProductOptionGroupValidationErrors>({});

  const [imageId, setImageId] = useState('');

  useEffect(() => {
    const enabled = product?.productVariantSetting?.enabled;
    const options = product?.productVariantSetting?.options;
    const variants = product?.productVariantSetting?.variants;
    if (!product || !enabled || !options?.length || !variants?.length) return;
    const aux = {};
    options.forEach(o => (aux[o.name] = o.values?.[0]?.name));
    setSelectedOptions(aux);
  }, [product]);

  // Initialize product options when product changes
  useEffect(() => {
    if (!product?.productOptionSetting?.enabled) return;
    if (!product?.productOptionSetting?.groups?.length) return;

    setSelectedProductOptions({});
    setOptionValidationErrors({});
    return;
  }, [product]);

  useEffect(() => {
    if (!product.images || !product.images.length) {
      const mainImageId = product.mainImageId;
      setImageId(mainImageId);
      return;
    }
    const firstImageId = product?.images?.[0]?.id;

    if (firstImageId) setImageId(firstImageId);
  }, [product]);

  const selectedProductVariant = useMemo(() => {
    const enabled = product?.productVariantSetting?.enabled;
    const variants = product?.productVariantSetting?.variants;
    if (!product || !enabled || !variants || !selectedOptions) return undefined;
    return variants.find(v => {
      const keys = Object.keys(v.combinations || {});
      return keys.every(k => v.combinations[k] === selectedOptions[k]);
    });
  }, [product, selectedOptions]);

  useEffect(() => {
    if (selectedProductVariant?.imageId) {
      setImageId(selectedProductVariant.imageId);
    }
  }, [selectedProductVariant]);

  const handleSelectOption = (criteria: string, option: string) => {
    setSelectedOptions(prev => {
      const aux = {...prev};
      if (aux[criteria] === option) aux[criteria] = '';
      else aux[criteria] = option;
      return aux;
    });
  };

  // Product options handlers - using groupIndex and optionIndex
  const handleProductOptionToggle = useCallback(
    (groupId: string, optionId: string) => {
      setSelectedProductOptions(prev => {
        const newState = {...prev};
        if (!newState[groupId]) newState[groupId] = {};
        if (!newState[groupId][optionId]) {
          newState[groupId][optionId] = {
            selected: false,
            quantity: 1,
            textValue: '',
          };
        }
        newState[groupId][optionId].selected = !newState[groupId][optionId]
          .selected;
        return newState;
      });
    },
    []
  );

  const handleProductOptionQuantityChange = useCallback(
    (groupId: string, optionId: string, quantity: number) => {
      setSelectedProductOptions(prev => {
        const newState = {...prev};
        if (!newState[groupId]) newState[groupId] = {};
        if (!newState[groupId][optionId]) {
          newState[groupId][optionId] = {
            selected: false,
            quantity: 1,
            textValue: '',
          };
        }
        newState[groupId][optionId].quantity = Math.max(0, quantity);
        return newState;
      });
    },
    []
  );

  const handleProductOptionTextChange = useCallback(
    (groupId: string, optionId: string, textValue: string) => {
      setSelectedProductOptions(prev => {
        const newState = {...prev};
        if (!newState[groupId]) newState[groupId] = {};
        if (!newState[groupId][optionId]) {
          newState[groupId][optionId] = {
            selected: false,
            quantity: 1,
            textValue: '',
          };
        }
        newState[groupId][optionId].textValue = textValue;
        return newState;
      });
    },
    []
  );

  const currentStock = useMemo(() => {
    const stockSetting = product?.stockSetting;
    if (!stockSetting?.enabled) return -1;
    if (product?.productVariantSetting?.enabled) {
      if (typeof selectedProductVariant?.stock === 'number') {
        return selectedProductVariant.stock;
      }
      return -1;
    }
    if (typeof stockSetting.stock === 'number') return stockSetting.stock;
    return -1;
  }, [product, selectedProductVariant]);

  const [quantity, setQuantity] = useState(1);

  const totalQuantity = useMemo(() => {
    const items = shoppingCart?.items;
    if (!product || !items?.length) return quantity;
    const found = items.find(i => {
      const sameProduct = i.product.id === product.id;
      const sameVariant = selectedProductVariant?.id
        ? i.productVariant && selectedProductVariant.id === i.productVariant.id
        : true;
      return sameProduct && sameVariant;
    });
    const quantityInShoppingCart = found ? found.amount : 0;
    return quantity + quantityInShoppingCart;
  }, [product, quantity, selectedProductVariant, shoppingCart]);

  const productOptionRequest: ShoppingCartProductOptionRequest[] = useMemo(() => {
    const optionsRequest = [];

    Object.entries(selectedProductOptions).forEach(([groupId, options]) => {
      Object.entries(options).forEach(([optionId, option]) => {
        if (option.selected) {
          optionsRequest.push({
            productOptionGroupId: groupId,
            productOptionId: optionId,
            quantity: option.quantity,
            text: option.textValue || undefined,
          });
        }
      });
    });
    return !!optionsRequest.length ? optionsRequest : undefined;
  }, [selectedProductOptions]);

  const selectedProductVariantId = selectedProductVariant?.id;

  const buildShoppingCartPricesRequest = useCallback(() => {
    const items = [];
    if (shoppingCart && shoppingCart.items && shoppingCart.items.length) {
      shoppingCart.items.forEach(i => {
        items.push({
          productId: i.product.id,
          productVariantId: i.productVariant?.id,
          quantity: i.amount,
        });
      });
    }
    if (product) {
      items.push({
        productId: product.id,
        productVariantId: selectedProductVariantId,
        quantity,
        productOptions: productOptionRequest,
      });
    }
    const shoppingCartPricesRequest: ShoppingCartPricesRequest = {items};
    return shoppingCartPricesRequest;
  }, [
    product,
    productOptionRequest,
    quantity,
    selectedProductVariantId,
    shoppingCart,
  ]);

  useEffect(() => {
    if (!product) return;
    const variantsEnabled = product?.productVariantSetting?.enabled;
    if (variantsEnabled && !selectedProductVariant) return;
    const shoppingCartPricesRequest: ShoppingCartPricesRequest = buildShoppingCartPricesRequest();
    fetchProductPrice({shoppingCartPricesRequest});
  }, [
    buildShoppingCartPricesRequest,
    fetchProductPrice,
    product,
    selectedProductVariant,
  ]);

  const productPrice = useMemo(() => {
    if (!product) return undefined;
    if (product.productVariantSetting?.enabled) {
      return selectedProductVariant?.price;
    }
    return product.price;
  }, [product, selectedProductVariant]);

  const productFetchedPrice = useMemo(() => {
    if (!product || !itemPrices?.length) return '';
    const found = itemPrices.find(i =>
      selectedProductVariant
        ? i.item.productVariant === selectedProductVariant.id
        : i.item.product === product.id
    );
    return found?.item?.price ? found.item.price.unitPrice : '';
  }, [itemPrices, product, selectedProductVariant]);

  const hasDiscount = useMemo(() => {
    if (!productPrice || !productFetchedPrice) return false;
    return parseFloat(productPrice) > parseFloat(productFetchedPrice);
  }, [productFetchedPrice, productPrice]);

  const productLowerPrice = useMemo(
    () => (product ? getProductItemPricing(product) : undefined),
    [product]
  );

  const handleConfirm = () => {
    setOptionValidationErrors({});

    if (hasProductOptions && product?.productOptionSetting?.groups) {
      const validationErrors = validateAllProductOptionGroups(
        product.productOptionSetting.groups,
        selectedProductOptions
      );

      if (hasValidationErrors(validationErrors)) {
        setOptionValidationErrors(validationErrors);
        return;
      }
    }

    onAddToCart(product, {
      productVariant: selectedProductVariant,
      quantity,
      productOptions: productOptionRequest,
    });
  };

  const handleCloseModal = () => {
    setQuantity(1);
    onClose();
  };

  const variantsEnabled = Boolean(product?.productVariantSetting?.enabled);

  const isOpen = Boolean(open && product);
  const currencySymbol = currency?.symbol || '';

  const selectedOptionsLabel = useMemo(() => {
    const values = Object.values(selectedOptions || {});
    return values.length ? ` - ${values.join('/')}` : '';
  }, [selectedOptions]);

  const hasVariants = Boolean(
    product?.productVariantSetting?.enabled &&
      product?.productVariantSetting?.options?.length &&
      product?.productVariantSetting?.variants?.length
  );

  const hasProductOptions = Boolean(
    product?.productOptionSetting?.enabled &&
      product?.productOptionSetting?.groups?.length
  );

  const shouldShowFetchedUnitPrice =
    !variantsEnabled || (variantsEnabled && !!selectedProductVariant);

  const unitPriceRaw = shouldShowFetchedUnitPrice
    ? productFetchedPrice
      ? parseFloat(productFetchedPrice)
      : '--'
    : productLowerPrice?.price;

  const unitPriceFormatted = commaFormat(unitPriceRaw, currencySymbol);

  const originalUnitPriceFormatted = commaFormat(
    parseFloat(productPrice),
    currencySymbol
  );

  const effectiveUnitPrice = useMemo(() => {
    if (productFetchedPrice) return parseFloat(productFetchedPrice);
    if (productPrice) return parseFloat(productPrice);
    return undefined;
  }, [productFetchedPrice, productPrice]);

  const totalPriceNumber = useMemo(() => {
    if (typeof effectiveUnitPrice !== 'number') return undefined;
    const basePrice = effectiveUnitPrice * quantity;
    return basePrice;
  }, [effectiveUnitPrice, quantity]);

  const totalPriceFormatted = useMemo(() => {
    if (typeof totalPriceNumber === 'undefined') return '';
    return commaFormat(totalPriceNumber, currencySymbol);
  }, [totalPriceNumber, currencySymbol]);

  const originalTotalPriceFormatted = useMemo(() => {
    if (!hasDiscount || !productPrice) return '';
    return commaFormat(parseFloat(productPrice) * quantity, currencySymbol);
  }, [hasDiscount, productPrice, quantity, currencySymbol]);

  const quantityMaxValue = useMemo(() => {
    if (!product?.stockSetting?.enabled || currentStock <= 0) return undefined;
    return currentStock - (totalQuantity - quantity);
  }, [product, currentStock, totalQuantity, quantity]);

  const showOutOfStockMsg = Boolean(
    product?.stockSetting?.enabled &&
      ((currentStock > 0 && totalQuantity >= currentStock) ||
        currentStock === 0)
  );

  const outOfStockMessage = useMemo(() => {
    if (currentStock === 0) return 'Producto sin stock';
    const inCart = totalQuantity > quantity ? totalQuantity - quantity : 0;
    return `Sólo cuenta con ${currentStock} unidades disponibles. ${
      inCart ? `Ya tiene ${inCart} en el carrito` : ''
    }`;
  }, [currentStock, quantity, totalQuantity]);

  const bulkPrices = product?.bulkPriceSetting?.bulkPrices || [];
  const hasBulkPrices = Boolean(bulkPrices.length);

  const disableButton = useMemo(() => {
    if (!product) return true;
    const outOfStockNow = currentStock === 0;
    const exceedsStock = currentStock > 0 && totalQuantity > currentStock;
    const variantMissing = variantsEnabled && !selectedProductVariant;
    const variantDisabled =
      !!selectedProductVariant && !selectedProductVariant.enabled;
    const missingPrice = !productFetchedPrice;
    return (
      outOfStockNow ||
      exceedsStock ||
      variantMissing ||
      variantDisabled ||
      missingPrice
    );
  }, [
    product,
    currentStock,
    totalQuantity,
    variantsEnabled,
    selectedProductVariant,
    productFetchedPrice,
  ]);

  if (!product) return null;

  return (
    <CustomModal
      open={isOpen}
      handleCloseModal={handleCloseModal}
      classes={{
        paper: classes.modalPaper,
      }}
    >
      <div className={classes.root}>
        <Typography className={classes.title}>
          Variantes y opciones de producto
        </Typography>
        <Divider className={classes.divider} />
        <div className={classes.productInfo}>
          <img
            className={classes.productImage}
            src={getImageUrl(imageId)}
            alt=""
          />
          <div className={classes.productDetails}>
            <Typography className={classes.productName}>
              {`${product.name}${selectedOptionsLabel}`}
            </Typography>
            <Typography className={classes.subtitle}>
              Precio unitario
            </Typography>
            <div className={classes.productPrice}>
              <Typography className={classes.productNormalPrice}>
                {unitPriceFormatted}
              </Typography>
              {hasDiscount && originalUnitPriceFormatted && (
                <Typography className={`${classes.productDiscountedPrice}`}>
                  {originalUnitPriceFormatted}
                </Typography>
              )}
            </div>
            <div className={classes.quantityWrap}>
              <Typography className={classes.subtitle}>Cantidad</Typography>
              <div className={classes.quantityRow}>
                <div>
                  <Quantity
                    value={quantity}
                    maxValue={quantityMaxValue}
                    type={2}
                    setQuantity={setQuantity}
                    addHandler={() => setQuantity(quantity + 1)}
                    removeHandler={() => setQuantity(quantity - 1)}
                  />
                  {showOutOfStockMsg && (
                    <Typography className={classes.outOfStock}>
                      {outOfStockMessage}
                    </Typography>
                  )}
                </div>
                {productPrice && (
                  <div className={classes.totalPrice}>
                    <Typography className={classes.totalPriceSubtitle}>
                      Precio total
                    </Typography>
                    <Typography className={classes.priceTotal}>
                      {totalPriceFormatted}
                    </Typography>
                    {hasDiscount && (
                      <Typography
                        className={`${classes.productDiscountedPrice} ${classes.productDiscountedTotalPrice}`}
                      >
                        {originalTotalPriceFormatted}
                      </Typography>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {hasVariants && (
          <>
            <Typography
              className={classes.optionTitle}
              style={{marginTop: '16px'}}
            >
              Variantes:
            </Typography>
            {product.productVariantSetting?.options?.map(o => (
              <div key={o.name} className={classes.variantsWrap}>
                <Typography className={classes.optionTitle}>
                  {o.label}:
                </Typography>
                {o.values.map((v, idx) => {
                  const selected = v.name === selectedOptions[o.name];
                  return (
                    <CustomButton
                      key={`${o.name}-${idx}`}
                      className={`${classes.variantPill} ${
                        selected ? classes.variantSelected : ''
                      }`}
                      onClick={() => handleSelectOption(o.name, v.name)}
                    >
                      {v.label}
                    </CustomButton>
                  );
                })}
              </div>
            ))}
          </>
        )}
        {hasProductOptions && (
          <>
            <Typography
              className={classes.optionTitle}
              style={{marginTop: '16px'}}
            >
              Opciones de producto:
            </Typography>
            <div className={classes.optionsContainer}>
              {product.productOptionSetting?.groups?.map(
                (group, groupIndex) => {
                  if (!group.options?.length) return null;
                  return (
                    <ProductOptionsRenderer
                      key={groupIndex}
                      group={group}
                      groupIndex={groupIndex}
                      selectedProductOptions={selectedProductOptions}
                      currencySymbol={currencySymbol}
                      validationErrors={optionValidationErrors[group.id] || []}
                      onOptionToggle={(groupId, optionIndex) =>
                        handleProductOptionToggle(groupId, optionIndex)
                      }
                      onTextChange={(groupId, optionIndex, value) =>
                        handleProductOptionTextChange(
                          groupId,
                          optionIndex,
                          value
                        )
                      }
                      onQuantityChange={(groupId, optionIndex, qty) =>
                        handleProductOptionQuantityChange(
                          groupId,
                          optionIndex,
                          qty
                        )
                      }
                    />
                  );
                }
              )}
            </div>
          </>
        )}
        {hasBulkPrices && (
          <div className={classes.bulkPriceTable}>
            <div className={classes.bulkPricesTitle}>
              <ShoppingCartOutlined
                className={classes.bulkPricesTitleIcon}
                color="primary"
              />
              <Typography className={classes.bulkPricesTitleText}>
                Precios al por mayor
              </Typography>
            </div>
            <table className={classes.bulkPricesTableLines}>
              <thead>
                <tr>
                  <th className={classes.bulkPricesTableLineHead}>Cantidad</th>
                  <th className={classes.bulkPricesTableLineHead}>
                    Precio por producto
                  </th>
                </tr>
              </thead>
              <tbody>
                {bulkPrices.map((element, index) => (
                  <tr key={index}>
                    <td className={classes.bulkPricesTableLineData}>
                      A partir de {element.minQuantity} unidades
                    </td>
                    <td className={classes.bulkPricesTableLineData}>
                      {commaFormat(
                        parseFloat(element.unitPrice),
                        currencySymbol
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <footer className={classes.footer}>
          <CustomButton
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            onClick={handleConfirm}
            disabled={disableButton}
          >
            Agregar al pedido
          </CustomButton>
        </footer>
      </div>
    </CustomModal>
  );
}
